import { updateKeyInStore } from 'App';
import * as Yup from 'yup';
import {
  requiredFileIf,
  requiredFiles,
} from '../../../useValidateQuestionnaire';
import { LogisticsStateValues, updateKey } from './slice';

export const fields: IBaseField<keyof LogisticsStateValues>[] = [
  {
    type: 'radio',
    name: 'annualTaxSummaryOutsideUs',
    label:
      'Do you have an annual tax summary (ie local W2) from any country outside of the U.S?',
    subLabel: () => (
      <p>Self-assessment tax declaration, government assessment notice, etc</p>
    ),
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'upload',
        name: 'residentCountryTaxCopy',
        label:
          'Please upload a copy of the RESIDENT COUNTRY tax declaration (tax return) and if available, the official Income Tax Assessment form for the filing year.',

        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
      },
    ],
  },
  {
    type: 'radio',
    name: 'IrsSentPin',
    label: 'Has the IRS sent you an Identity Protection PIN?',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'text',
        name: 'enterIrsSentPin',
        label: 'Please enter the PIN',
        subLabel: () => <p>These are six digits long</p>,
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Enter',
      },
    ],
  },
  {
    type: 'select',
    name: 'refundMethod',
    label: 'Select your Refund method',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    options: [
      {
        label: 'Direct Deposit to US Bank Account',
        value: 'Direct Deposit to US Bank Account',
      },
      { label: 'Check Mailed to You', value: 'Check Mailed to You' },
      {
        label: 'Apply to estimated payments',
        value: 'Apply to estimated payments',
      },
    ],
    dependencies: [
      {
        type: 'select',
        name: 'uploadCheckOrManuallyWriteBank',
        label:
          'Upload voided check or manually write in bank routing and account number? This number will not be used to make a direct debit or to pay any amount due to the IRS.',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        options: [
          { label: 'Upload Void Check', value: 'Upload Void Check' },
          { label: 'Manually write-in', value: 'Manually write-in' },
        ],
        predicate: (value) => value === 'Direct Deposit to US Bank Account',
        dependencies: [
          {
            type: 'text',
            name: 'bankName',
            label: 'Name of Bank',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'Manually write-in',
            placeholder: 'Enter',
          },
          {
            type: 'text',
            name: 'routingNumber',
            label: 'Routing Number',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'Manually write-in',
            placeholder: 'Enter',
          },
          {
            type: 'text',
            name: 'accountNumber',
            label: 'Account Number',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'Manually write-in',
            placeholder: 'Enter',
          },
          {
            type: 'select',
            name: 'checkingOrSavingAccount',
            label: 'Is this a checking or savings account?',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            options: [
              { label: 'Savings', value: 'Savings' },
              { label: 'Checking', value: 'Checking' },
            ],
            predicate: (value) => value === 'Manually write-in',
          },
          {
            type: 'radio',
            name: 'useThisAccountForDirectWithdrawalOfTaxBalanceDue',
            label: 'Use this account for Direct Withdrawal of tax balance due?',
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'Manually write-in',
          },
          {
            type: 'upload',
            name: 'checkCopy',
            label: 'Upload Check',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'Upload Void Check',
          },
        ],
      },
    ],
  },
];
const requiredIfManualWriteBank = (schema: Yup.AnySchema) => {
  return schema.when(['refundMethod', 'uploadCheckOrManuallyWriteBank'], {
    is: (refundMethod: string, uploadCheckOrManuallyWriteBank: string) => {
      return (
        refundMethod === 'Direct Deposit to US Bank Account' &&
        uploadCheckOrManuallyWriteBank === 'Manually write-in'
      );
    },
    then: schema.required('required'),
  });
};

export const validationSchema = Yup.object().shape({
  refundMethod: Yup.string().nullable().trim().required('required'),
  uploadCheckOrManuallyWriteBank: Yup.string()
    .nullable()
    .when('refundMethod', {
      is: 'Direct Deposit to US Bank Account',
      then: Yup.string().nullable().required('required'),
    }),
  bankName: requiredIfManualWriteBank(Yup.string().nullable().trim()),
  routingNumber: requiredIfManualWriteBank(Yup.string().nullable().trim()),
  accountNumber: requiredIfManualWriteBank(Yup.string().nullable().trim()),
  checkingOrSavingAccount: requiredIfManualWriteBank(
    Yup.string().nullable().trim(),
  ),
  checkCopy: requiredFileIf(
    ['refundMethod', 'uploadCheckOrManuallyWriteBank'],
    (refundMethod: string, uploadCheckOrManuallyWriteBank: string) => {
      return (
        refundMethod === 'Direct Deposit to US Bank Account' &&
        uploadCheckOrManuallyWriteBank === 'Upload Void Check'
      );
    },
  ),
});
